import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import swal from "sweetalert";
import { FormSkeleton, TableSkeletonRows } from "../loader";
import { aiService } from "../../service/api.service";
import AddKnowledgeBaseModal from "./AddKnowledgeBaseModal";
import AssignNumberModal from "./AssignNumberModal";
import { EmptyTableState } from "../common/EmptyTableState";
import { StyledTable, StyledTableHeader, StyledTableCell, StyledTableRow } from "../common/StyledTable";
import { KnowledgeBaseStatusBadge, AgentStatusBadge } from "../common/StatusBadge";

function formatValue(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "object") return JSON.stringify(value);
  if (String(value).match(/^\d{4}-\d{2}-\d{2}T/)) return new Date(value).toLocaleString();
  return value;
}

function DetailItem({ label, value }) {
  return (
    <Col md={4} className="agent-info-detail-item">
      <span className="text-secondary">{label}</span>
      <h6 className="mt-1">{formatValue(value)}</h6>
    </Col>
  );
}

export default function AgentInfo() {
  const location = useLocation();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(location.state?.agent || null);
  const [loading, setLoading] = useState(false);
  const [kbLoading, setKbLoading] = useState(false);
  const [knowledgeData, setKnowledgeData] = useState([]);
  const [knowledgeTotal, setKnowledgeTotal] = useState(0);
  const [knowledgeBaseModal, setKnowledgeBaseModal] = useState(false);
  const [assignNumberModal, setAssignNumberModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const agentId = location.state?.agentId || location.state?.agent?._id || searchParams.get("agent_id");
  const agentName = agent?.name || location.state?.agentName || searchParams.get("agent_name") || "";
  const returnToAllAgents = location.state?.returnToAllAgents ?? searchParams.get("from") === "all";
  const fromUserInfo = location.state?.fromUserInfo ?? searchParams.get("from") === "company-info";
  const userId = location.state?.userId || searchParams.get("user_id") || agent?.user;
  const userName = location.state?.userName || searchParams.get("user_name") || agent?.owner_name || "";
  const userEmail = location.state?.userEmail || searchParams.get("user_email") || agent?.owner_email || "";

  const handleUnauthorized = () => {
    swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
      localStorage.clear();
      window.location.href = "/";
    });
  };

  const goBack = useCallback(() => {
    if (returnToAllAgents) {
      navigate("/allagentlist");
      return;
    }

    if (userId) {
      const query = new URLSearchParams({ user_id: userId });
      if (userName) query.append("user_name", userName);
      navigate(`/agent-list?${query.toString()}`, { state: { userId, userName, userEmail } });
      return;
    }

    navigate("/allagentlist");
  }, [navigate, returnToAllAgents, userEmail, userId, userName]);

  const loadAgent = useCallback(async () => {
    if (!agentId) {
      swal({ icon: "error", text: "Please select an agent to view their details." }).then(() => navigate("/allagentlist"));
      return;
    }

    setLoading(true);
    try {
      const res = await aiService.getAgent(agentId);
      let agentData = res?.data?.data || {};

      try {
        const listRes = await aiService.getAgents({
          all: true,
          page: 1,
          page_size: 1000,
          deleted: agentData.is_deleted,
        });
        const listedAgent = (listRes?.data?.data || []).find((a) => a._id === agentId);
        if (listedAgent) {
          agentData = { ...agentData, ...listedAgent };
        }
      } catch {
        // Base agent details are still useful if the enriched list request fails.
      }

      setAgent(agentData);
      setKnowledgeData(agentData.knowledge || []);
      setKnowledgeTotal((agentData.knowledge || []).length);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({ icon: "error", text: error?.response?.data?.message || "Failed to load agent details." }).then(goBack);
      }
    } finally {
      setLoading(false);
    }
  }, [agentId, goBack, navigate]);

  const loadKnowledgeBase = useCallback(async () => {
    if (!agentId) return;
    setKbLoading(true);
    try {
      const res = await aiService.listKnowledgeBase({ agent_id: agentId, limit: 100, page: 1 });
      setKnowledgeData(res?.data?.data || []);
      setKnowledgeTotal(res?.data?.total_count || 0);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        swal({ icon: "error", text: error?.response?.data?.message || "Failed to load knowledge base." });
      }
    } finally {
      setKbLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  useEffect(() => {
    loadKnowledgeBase();
  }, [loadKnowledgeBase]);

  const editAgent = () => {
    navigate("/agent-create", {
      state: {
        userId: agent?.user || userId,
        userName: agent?.owner_name || userName,
        userEmail: agent?.owner_email || userEmail,
        agentName,
        agentId,
        agent,
        returnToAllAgents,
        returnToAgentInfo: true,
        fromUserInfo,
      },
    });
  };

  const toggleAgentStatus = () => {
    if (!agent) return;
    const isEnabled = agent.status !== "Disabled";
    const nextStatus = isEnabled ? "Disabled" : "Enabled";
    swal({
      title: "Confirmation",
      text: `Are you sure you want to ${isEnabled ? "disable" : "enable"} this agent?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setStatusUpdating(true);
      try {
        await aiService.changeAgentStatus(agentId, { status: nextStatus });
        swal({
          icon: "success",
          text: `Agent ${isEnabled ? "disabled" : "enabled"} successfully.`,
        });
        setAgent((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      } catch (error) {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        } else {
          swal({
            icon: "error",
            text: error?.response?.data?.message || "Failed to update agent status.",
          });
        }
      } finally {
        setStatusUpdating(false);
      }
    });
  };

  const handleDeleteAgent = () => {
    if (!agent) return;
    swal({
      title: "Confirmation",
      text: `Are you sure you want to delete the agent "${agentName}"? It will be hidden everywhere until it's restored.`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setStatusUpdating(true);
      try {
        await aiService.deleteAgent(agentId);
        swal({ icon: "success", text: "Agent deleted successfully." });
        setAgent((prev) => (prev ? { ...prev, is_deleted: true } : prev));
      } catch (error) {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        } else {
          swal({
            icon: "error",
            text: error?.response?.data?.message || "Failed to delete agent.",
          });
        }
      } finally {
        setStatusUpdating(false);
      }
    });
  };

  const handleRestoreAgent = () => {
    if (!agent) return;
    swal({
      title: "Confirmation",
      text: `Restore the agent "${agentName}"?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setStatusUpdating(true);
      try {
        await aiService.restoreAgent(agentId);
        swal({ icon: "success", text: "Agent restored successfully." });
        setAgent((prev) => (prev ? { ...prev, is_deleted: false } : prev));
      } catch (error) {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        } else {
          swal({
            icon: "error",
            text: error?.response?.data?.message || "Failed to restore agent.",
          });
        }
      } finally {
        setStatusUpdating(false);
      }
    });
  };

  const handleDeleteKnowledge = (kb) => {
    swal({
      title: "Confirmation",
      text: `Delete knowledge base file "${kb.filename}"?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setKbLoading(true);
      try {
        await aiService.deleteKnowledgeBase({ agent_id: agentId, knowledge_base_id: kb._id });
        swal({ icon: "success", text: "Knowledge base deleted successfully." });
        loadKnowledgeBase();
      } catch (error) {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        } else {
          swal({ icon: "error", text: error?.response?.data?.message || "Failed to delete knowledge base." });
        }
      } finally {
        setKbLoading(false);
      }
    });
  };

  const detailFields = [
    ["Agent ID", agent?._id],
    ["Name", agent?.name],
    ["Owner", agent?.owner_name || userName],
    ["Owner Email", agent?.owner_email || userEmail],
    ["Purpose", agent?.purpose],
    ["Tone", agent?.tone],
    ["Welcome Message", agent?.welcomeMsg],
    ["Guidance", agent?.systemPrompt],
    ["Uses Emojis", agent?.emojis],
    ["Lead Generation", agent?.leadGeneration],
    ["Qualification Questions", agent?.qualification_questions],
    ["Escalation Rules", agent?.escalation_rules],
    ["Created At", agent?.created_at],
    ["Updated At", agent?.updated_at],
    ["Created By", agent?.created_by],
    ["User ID", agent?.user],
  ];
  const pageTitle = agentName || "Agent Info";

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="under-content-section">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div>
                <h1 className="company-page-title">{pageTitle}</h1>
              </div>
            </div>
            <div className="header-actions-right" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {agent ? <AgentStatusBadge status={agent.status} isDeleted={agent.is_deleted} /> : null}
              {agent?.is_deleted ? (
                <Button
                  variant="outline-secondary"
                  className="agent-primary-btn"
                  onClick={handleRestoreAgent}
                  disabled={!agent || statusUpdating}
                >
                  Restore Agent
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline-secondary"
                    className="agent-primary-btn"
                    onClick={toggleAgentStatus}
                    disabled={!agent || statusUpdating}
                  >
                    {agent?.status === "Disabled" ? "Enable Agent" : "Disable Agent"}
                  </Button>
                  <Button
                    variant="outline-danger"
                    className="agent-primary-btn"
                    onClick={handleDeleteAgent}
                    disabled={!agent || statusUpdating}
                  >
                    Delete Agent
                  </Button>
                  <Button variant="primary" className="agent-primary-btn" onClick={editAgent} disabled={!agentId}>
                    Edit
                  </Button>
                </>
              )}
            </div>
          </div>

          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <div className="audit-request-box premium-company-card user-info-detail-card agent-info-detail-card">
                {loading && !agent ? (
                  <FormSkeleton rows={4} />
                ) : (
                  <Row>
                    {detailFields.map(([label, value]) => (
                      <DetailItem key={label} label={label} value={value} />
                    ))}
                  </Row>
                )}
              </div>
            </Col>
          </Row>

          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <div className="agent-info-section-heading">
                <h5 className="company-page-title user-info-section-title">Assigned Phone Numbers</h5>
                <Button variant="primary" className="agent-primary-btn" onClick={() => setAssignNumberModal(true)} disabled={!agent}>
                  Manage Phone Number
                </Button>
              </div>
              <div className="audit-request-box premium-company-card">
                <StyledTable responsive="sm" className="UserListTable premium-user-list-table user-info-numbers-table">
                  <thead>
                    <StyledTableRow>
                      <StyledTableHeader>Phone Number</StyledTableHeader>
                      <StyledTableHeader>Phone ID</StyledTableHeader>
                    </StyledTableRow>
                  </thead>
                  <tbody>
                    {loading ? (
                      <TableSkeletonRows rows={1} columns={2} />
                    ) : agent?.phone ? (
                      <StyledTableRow>
                        <StyledTableCell>{agent.phone}</StyledTableCell>
                        <StyledTableCell>{agent.phone_id || "-"}</StyledTableCell>
                      </StyledTableRow>
                    ) : (
                      <EmptyTableState colSpan={2} />
                    )}
                  </tbody>
                </StyledTable>
              </div>
            </Col>
          </Row>

          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <div className="agent-info-section-heading">
                <h5 className="company-page-title user-info-section-title">Knowledge Base</h5>
                <Button variant="primary" className="agent-primary-btn" onClick={() => setKnowledgeBaseModal(true)} disabled={!agent}>
                  Add Knowledge Base
                </Button>
              </div>
              <div className="audit-request-box premium-company-card">
                <StyledTable responsive="sm" className="UserListTable premium-user-list-table knowledge-base-table agent-info-kb-table">
                  <thead>
                    <StyledTableRow>
                      <StyledTableHeader>Filename</StyledTableHeader>
                      <StyledTableHeader>Status</StyledTableHeader>
                      <StyledTableHeader>Owner</StyledTableHeader>
                      <StyledTableHeader>Uploaded By</StyledTableHeader>
                      <StyledTableHeader>Uploaded At</StyledTableHeader>
                      <StyledTableHeader>Action</StyledTableHeader>
                    </StyledTableRow>
                  </thead>
                  <tbody>
                    {kbLoading ? (
                      <TableSkeletonRows rows={5} columns={6} />
                    ) : knowledgeData.length > 0 ? (
                      knowledgeData.map((kb) => (
                        <StyledTableRow key={kb._id}>
                          <StyledTableCell>{kb.url ? <a href={kb.url} target="_blank" rel="noreferrer">{kb.filename}</a> : kb.filename}</StyledTableCell>
                          <StyledTableCell><KnowledgeBaseStatusBadge status={kb.status} /></StyledTableCell>
                          <StyledTableCell>{kb.owner_name || "-"}</StyledTableCell>
                          <StyledTableCell>{kb.uploaded_by_name || "-"}</StyledTableCell>
                          <StyledTableCell>{kb.created_at ? new Date(kb.created_at).toLocaleString() : "-"}</StyledTableCell>
                          <StyledTableCell>
                            <Button size="sm" variant="light" title="Delete Knowledge Base" onClick={() => handleDeleteKnowledge(kb)}>
                              <i className="fa fa-trash text-danger" aria-hidden="true"></i>
                            </Button>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <EmptyTableState colSpan={6} />
                    )}
                  </tbody>
                </StyledTable>
                <div className="text-secondary agent-info-table-count">{knowledgeTotal} knowledge base file{knowledgeTotal === 1 ? "" : "s"}</div>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <AddKnowledgeBaseModal
        show={knowledgeBaseModal}
        handleClose={() => setKnowledgeBaseModal(false)}
        agent={agent}
        onSuccess={() => {
          loadAgent();
          loadKnowledgeBase();
        }}
      />

      <AssignNumberModal
        show={assignNumberModal}
        handleClose={() => setAssignNumberModal(false)}
        agent={agent}
        onSuccess={loadAgent}
      />
    </div>
  );
}
